import { View } from "@westpierstudio/luma/mvc";
import { getEase, Tween } from "@westpierstudio/luma/createjs";
import { GameServices } from "game/GameServices";
import { ReelSymbol } from "./ReelSymbol";
import { SpinConfig } from "./ReelsModel";
import { OverlayID, OverlaySymbols, ReelSymbols, ServerOverlayID, SymbolID } from "servertypes/SharedTypes";
import { mod } from "utils/Misc";
import { Signal } from "type-signals";
import { SymbolCache } from "./Reels";
import { random, sample } from "lodash";
import { Spine } from "pixi-spine";
import { ElasticCache, ExternalItem } from "@westpierstudio/luma/utils/elastic-cache";
import { Container } from "pixi.js";

const SYMBOL_COUNT = 5; //0 is offscreen, 1-4 are in view

enum State {
    STOP,
    MOVE,
    BOUNCE
}

class ReelSignals {
    reelStop = new Signal<(reelIndex: number) => void>();
    reelStopSound = new Signal();
    coinHit = new Signal();
    scatterWinComplete = new Signal();
    sparkleRequest = new Signal<(reelIndex: number, distance: number, reelSpeed?: number | undefined) => void>();
    dragonRoar = new Signal<(inAnimLoop: boolean) => void>();
    pandaRoar = new Signal<(inAnimLoop: boolean) => void>();
}

export class Reel extends View {
    private readonly defaultY: number;
    private readonly symbols: ReelSymbol[] = [];
    private reelStrip: SymbolID[] = [];
    private reelStripIndex = 0;
    private stopIndex = 0;
    private enableSpinByOverlays = false;
    private overlayResult!: OverlaySymbols;
    private swappedSymbols: {index: number, symbol: SymbolID}[] = [];
    private overlayResultIndex = -1;
    private spinConfig!: SpinConfig;
    private state = State.STOP;
    signals = new ReelSignals();

    constructor(services: GameServices, private readonly id: number, private readonly symbolHeight: number,
        symbolCache: SymbolCache, overlayCache: ElasticCache<Spine & Container>, overlaysResetMap: Map<number, ExternalItem<Spine & Container>>,
        overlayFrameCache: ElasticCache<Spine & Container>, overlaysFrameResetMap: Map<number, ExternalItem<Spine & Container>>, private readonly spinByOverlayWeight: number,
        private readonly reelIndex: number) {
        super(services);

        let symbol;

        for (let i=0; i<SYMBOL_COUNT; i++) {

            symbol = new ReelSymbol(services, symbolCache, overlayCache, overlaysResetMap, overlayFrameCache, overlaysFrameResetMap);
            symbol.y = i * this.symbolHeight;

            symbol.signals.scatterWinComplete.add(() => this.signals.scatterWinComplete.dispatch());
            symbol.signals.dragonRoar.add((inAnim: boolean) => { this.signals.dragonRoar.dispatch(inAnim); }, this);
            symbol.signals.pandaRoar.add((inAnim: boolean) => { this.signals.pandaRoar.dispatch(inAnim); }, this);

            this.addChild(symbol);
            this.symbols.push(symbol);
        }

        this.y += symbolHeight/2;
        this.defaultY = this.y;
    }

    get totalSpinTime(): number {

        const {anticipationActive, anticipationTime, baseSpinTime, reelStopDelay, reelStopTimeIn, reelStopTimeOut} = this.spinConfig;

        let spinTime = baseSpinTime + (this.id * reelStopDelay);
        const stopTime = reelStopTimeIn + reelStopTimeOut;

        if (anticipationActive) {
            spinTime += anticipationTime;
        }

        return spinTime + stopTime;
    }

    getVisibleSymbolGlobalPosition(symbolIndex: number) {
        const symbol = this.symbols[symbolIndex + 1];
        return this.toGlobal(symbol.position);
    }

    setSymbols(symbols: ReelSymbols, overlays: OverlaySymbols, slamStop: boolean=false): void {

        for (const [index, symbolID] of symbols.entries()) {
            this.symbols[index+1].setSymbol(symbolID, overlays[index], false);

            if (slamStop && overlays[index] !== `E`) {
                this.signals.sparkleRequest.dispatch(this.reelIndex, index+1);
            }
        }
    }

    setReelStrip(reelStrip: SymbolID[]): void {

        this.restoreSwappedSymbols(); //Restore any swapped out symbols from the previous spin prior to a strip switch
        this.reelStrip = reelStrip;
    }

    setOverlayResult(overlayResult: OverlaySymbols): void {
        this.overlayResult = overlayResult;
    }

    setSpinConfig(spinConfig: SpinConfig): void {
        this.spinConfig = spinConfig;
    }

    getRandomReelStripIndex(): number {
        return Math.floor(Math.random() * this.reelStrip.length);
    }

    swapReelStripSymbols(startIndex: number, symbols: SymbolID[]): void {

        //Restore the last swapped symbols prior to the new swap to prevent persistent changes
        this.restoreSwappedSymbols();

        let index = startIndex;

        for (const [i, symbol] of symbols.entries()) {

            this.swappedSymbols.push({index, symbol: this.reelStrip[index]});
            this.reelStrip[index] = symbol;

            index++;
            index = index % this.reelStrip.length;
        }
    }

    spin(stopIndex: number): void {

        this.stopIndex = stopIndex;
        this.enableSpinByOverlays = true;

        const {anticipationActive, anticipationTime, baseSpinTime, reelStopDelay, symbolsPerSecond} = this.spinConfig;

        let totalMoveTimeMS = baseSpinTime + (this.id * reelStopDelay);

        if (anticipationActive) {
            totalMoveTimeMS += anticipationTime;
        }

        const totalDistance = (symbolsPerSecond * this.symbolHeight) * (totalMoveTimeMS/1000);
        const symbolMoveCount = Math.floor(totalDistance/this.symbolHeight);
        const oneSymbolMoveTimeMS = (symbolMoveCount > 0) ? totalMoveTimeMS/symbolMoveCount : 0;

        //We want the stopindex to correspond with the bottom symbol so include a shift down of 4
        this.reelStripIndex = mod(stopIndex - symbolMoveCount + 4, this.reelStrip.length);

        this.setNextOffScreenSymbol();
        this.startMoveTween(oneSymbolMoveTimeMS, symbolMoveCount);
    }

    slamStop(symbols: ReelSymbols, overlays: OverlaySymbols): void {

        if (this.state === State.MOVE) {

            Tween.removeTweens(this);
            this.y = this.defaultY;

            this.setSymbols(symbols, overlays, true);
            this.stopWithBounce();
        }
    }

    showOverlays(): void {

        for (const [i, symbol] of this.symbols.entries()) {
            if (i > 0) {
                symbol.showOverlay();
            }
        }
    }

    setSymbolToAlteredWild(symbolIndex: number, multiplier?: number): void {
        this.symbols[symbolIndex].setToAlteredWild(multiplier);
    }

    startWinAnimations(animateState: boolean[]): void {

        for (const [i, animate] of animateState.entries()) {
            if (animate) {
                this.symbols[i+1].startWinAnimLoop();
            }
        }
    }

    getWinSymbols(winState: boolean[]): string[] {

        const winners: string[] = [];

        for (const [i, winner] of winState.entries()) {

            if (winner) {
                const symbol = this.symbols[i+1];

                winners.push(symbol._id);
            }
        }

        return winners;
    }

    bonusWinAnimations(row: number) {
        this.symbols[row+1].bonusWinAnims();
    }

    startScatterWinAnimationSequence(row: number) {
        this.symbols[row+1].scatterWinSequence();
    }

    startScatterWinAnimationLoop(row: number, complete: boolean = true) {
        this.symbols[row+1].startScatterWinLoop(complete);
    }

    stopWinAnimations(): void {

        for (const [i, symbol] of this.symbols.entries()) {

            if (i > 0) {
                symbol.stopWinAnimLoop();
            }
        }
    }

    getSymbol(symbolIndex: number): ReelSymbol {
        return this.symbols[symbolIndex];
    }

    override reset(): void {

        this.reelStrip = [];
        this.reelStripIndex = 0;
        this.stopIndex = 0;
        this.enableSpinByOverlays = false;
        this.swappedSymbols = [];
        this.overlayResultIndex = -1;

        Tween.removeTweens(this);
        this.y = this.defaultY;
        this.state = State.STOP;

        for (const symbol of this.symbols) {
            symbol.reset();
        }
    }

    private startMoveTween(oneSymbolMoveTimeMS: number, symbolMoveCount: number): void {

        this.state = State.MOVE;
        const targetY = this.y + this.symbolHeight;
        const moveTween = Tween.get(this);

        while (symbolMoveCount > 0) {

            moveTween.to({y: targetY}, oneSymbolMoveTimeMS);
            moveTween.call(() => {
                this.setNextReelStripIndex();
                this.updateSymbolsInView();
                this.setNextOffScreenSymbol();
            });
            moveTween.to({y: this.defaultY}, 0);

            symbolMoveCount--;
        }

        moveTween.call(() => {
            this.signals.reelStopSound.dispatch();
            this.stopWithBounce();
        });
    }

    private restoreSwappedSymbols(): void {

        if (this.swappedSymbols.length > 0) {

            for (const item of this.swappedSymbols) {
                this.reelStrip[item.index] = item.symbol;
            }

            this.swappedSymbols = [];
        }
    }

    private setNextReelStripIndex(): void {

        this.reelStripIndex++;
        this.reelStripIndex = this.reelStripIndex % this.reelStrip.length;
    }

    private setNextOffScreenSymbol(): void {

        let overlaySymbol: OverlayID = `E`;
        /*
            Disable spinByOverlays once we reach symbols in the result window and start indexing the actual result.
            The stopIndex corresponds to the bottom symbol in the result window so is reached 1st.
        */
        if (this.reelStripIndex === this.stopIndex) {

            this.enableSpinByOverlays = false;
            this.overlayResultIndex = 0;
        }

        if (this.enableSpinByOverlays) {

            if (random(1, this.spinByOverlayWeight) === 1) {

                overlaySymbol = sample(Object.values(ServerOverlayID)) as ServerOverlayID;
                this.enableSpinByOverlays = false;
            }

        } else if (this.overlayResultIndex !== -1) {
            overlaySymbol = this.overlayResult[this.overlayResultIndex++];

            //reset index for those above the result
            if (this.overlayResultIndex >= this.overlayResult.length) {
                this.overlayResultIndex = -1;
            }
        }

        if (overlaySymbol !== `E`) {
            let distance = 5;
            const reelPos = this.reelStripIndex - this.stopIndex;
            if (reelPos >= 0 && reelPos <= 3) {
                distance = 4 - reelPos;
                this.signals.sparkleRequest.dispatch(this.reelIndex, distance);
            } else {
                const {symbolsPerSecond} = this.spinConfig;
                this.signals.sparkleRequest.dispatch(this.reelIndex, distance, symbolsPerSecond);
            }
        }

        this.symbols[0].setSymbol(this.reelStrip[this.reelStripIndex], overlaySymbol, false);
    }

    //Set each symbol id to the one preceding it
    private updateSymbolsInView(): void {

        let symbolAbove;

        for (let i=SYMBOL_COUNT-1; i>0; i--) {

            symbolAbove = this.symbols[i-1];
            this.symbols[i].setSymbol(symbolAbove.id, symbolAbove.overlayID, symbolAbove.alteredWild, false);
        }
    }

    private stopWithBounce(): void {

        this.state = State.BOUNCE;

        const {reelStopDistance, reelStopEaseIn, reelStopEaseOut, reelStopTimeIn, reelStopTimeOut} = this.spinConfig;

        Tween.get(this)
        .to({y: this.y + reelStopDistance}, reelStopTimeIn, getEase(reelStopEaseIn))
        .to({y: this.y}, reelStopTimeOut, getEase(reelStopEaseOut))
        .call(() => {
            this.state = State.STOP;
            this.signals.reelStop.dispatch(this.id);
        });

        for (const [i,symbol] of this.symbols.entries()) {
            if (i !== 0) {
                if (symbol._overlayID !== `E`) {
                    this.signals.coinHit.dispatch();
                }
                symbol.playLandAnim();
            }
        }
    }
}